import Tooltip from 'components/controls/Tooltip';
import { COLLABORATION_REQUEST_SENT_TEXT } from 'components/v2/compiler/constants';
import { StyledRequestCollaborationButton } from 'components/v2/buttons';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import React from 'react';
import styled, { css } from 'styled-components';
import { defaultThemeColors } from 'styles/theme';
import { Option } from 'types/types';
import { isWithin24Hours } from 'util/utils';

interface LcaCompanyCardProps {
  data: any;
  selectedPractitioners?: Option[] | undefined;
  companyPractitioners?: Option[] | undefined;
  onSelectPractitioner: any;
  onRequestCollaboration: any;
  onAddPractitioner: any;
  expanded: boolean;
  buttonsAreDisabled: boolean | undefined;
}

const LcaCompanyCard: React.FunctionComponent<LcaCompanyCardProps> = ({
  data,
  selectedPractitioners,
  companyPractitioners,
  onSelectPractitioner,
  onRequestCollaboration,
  onAddPractitioner,
  expanded,
  buttonsAreDisabled,
}) => {
  const { id, name, members, requestSentAt, successOperationText, errorText } = data;

  const collaborationRequestDate = !!requestSentAt ? new Date(requestSentAt) : undefined;

  const requestCollaborationButtonDisabledText =
    successOperationText ??
    (isWithin24Hours(collaborationRequestDate)
      ? COLLABORATION_REQUEST_SENT_TEXT
      : null);

  const headerTemplate = (options: any) => {
    const className = `${options.className}`;
    return (
      <div
        style={{ backgroundColor: options.collapsed ? '#FFFFFF' : '#F9F9FF' }}
        className={className}
        onClick={options.onTogglerClick}
      >
        <strong>{name}</strong>
        <StyledRequestCollaborationButton
          style={{ marginRight: 0, minWidth: '160px', visibility: expanded ? 'visible' : 'hidden' }}
          label="Request collaboration"
          onClick={(e) => {
            e.stopPropagation();
            onRequestCollaboration(id);
          }}
          disabled={buttonsAreDisabled || requestCollaborationButtonDisabledText}
        />
      </div>
    );
  };

  return (
    <StyledLcaCompanyCardPanel
      headerTemplate={(options) => headerTemplate(options)}
      toggleable
      collapsed={!expanded}
      onToggle={onSelectPractitioner}
      $collapsed={!expanded}
    >
      <div onClick={onSelectPractitioner}>
        <StyledPractitionersContainer>
          {members?.map((p: any) => (
            <StyledPractitionerContainer key={p.membershipId}>
              <EmailContainer>
                <Tooltip position={'titleEllipsed'}>{p.email}</Tooltip>
                {p.email}
              </EmailContainer>
              <StyledAddPractitionerButton
                label="Add practitioner"
                onClick={(e) => {
                  e.stopPropagation();
                  onAddPractitioner(p.membershipId);
                }}
                disabled={
                  buttonsAreDisabled ||
                  companyPractitioners?.some((companyPractitioner: Option) => companyPractitioner.value === p.userId) ||
                  members?.some((member: any) =>
                    selectedPractitioners?.some((practitioner: Option) => practitioner.value === member.userId)
                  )
                }
              />
            </StyledPractitionerContainer>
          ))}
        </StyledPractitionersContainer>
        {requestCollaborationButtonDisabledText && (
          <StyledSuccessText>{requestCollaborationButtonDisabledText}</StyledSuccessText>
        )}
        {errorText && <StyledErrorText>{errorText}</StyledErrorText>}
      </div>
    </StyledLcaCompanyCardPanel>
  );
};

const StyledLcaCompanyCardPanel = styled(Panel)<{ $collapsed: boolean }>`
  border: 1px solid;
  border-color: ${defaultThemeColors.elementsInputBorder};
  border-radius: 2px;

  min-height: 49px;
  align-content: center;

  .p-panel-header {
    padding-left: 1rem;
    border: 0;
    background-color: ${defaultThemeColors.primaryWhite};
    ${(props) => props.theme.typography.bodyInput};
    color: ${(props) => props.theme.colors.primaryBlack};
  }

  &.p-panel.p-panel-toggleable .p-panel-header {
    padding: 1rem;
  }

  .p-panel-content {
    padding: 1rem;
    border: 0;
    background-color: ${(props) =>
      props.$collapsed ? defaultThemeColors.primaryWhite : props.theme.colors.elementsTableRow};
    ${(props) => props.theme.typography.bodyInput};
    padding-top: 0px;
    padding-bottom: 1rem;
  }

  p {
    margin-top: 0.5rem;
  }

  ${(props) =>
    !props.$collapsed &&
    css`
      background: ${(props) => props.theme.colors.elementsTableRow};
    `}
`;

const StyledPractitionersContainer = styled.div`
  margin-top: 15px;
`;

const StyledPractitionerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  &:not(:last-child) {
    margin-bottom: 8px;
  }
`;

const StyledAddPractitionerButton = styled(Button)`
  ${(props) => props.theme.typography.buttonButtonSmall};
  height: 1.5rem;
  min-height: 22px;
  min-width: 110px;
  background-color: transparent !important;
  color: ${(props) => props.theme.colors.primaryOrange};
  border-radius: 2px;
  border: none;
  padding: 0px;

  &&:hover {
    color: ${(props) => props.theme.colors.elementsButtonHover};
  }

  &:disabled {
    color: ${(props) => props.theme.colors.elementsButtonDisabled};
  }
`;

const StyledSuccessText = styled.div`
  margin-top: 15px;
  font-size: 10px;
  font-style: italic;
  color: ${defaultThemeColors.primaryGreen};
`;

const StyledErrorText = styled.div`
  margin-top: 15px;
  color: red;
`;

const EmailContainer = styled.span`
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  margin-right: 1rem;
`;

export default LcaCompanyCard;
